import React, { useEffect, useState } from "react";
import { AiOutlineEdit, AiOutlineSend } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { Menu } from "@headlessui/react";
import { HiOutlinePlus } from "react-icons/hi2";
import {
    useGetAllNotificationTemplatesMutation,
    useUpdateStatusOfNotificationTemplateMutation,
    useSendNotificationMutation,
} from "../../../features/tenant/notificationTemplate/NotificationTemplateApi";
import toast from "react-hot-toast";
import { getVendorName } from "../../../utils/getVendorName";
import ImageModal from "../../../components/Tenant/ImageModal/ImageModal";
import view from "../../../assests/tenant/images/view.svg";
import SendNotificationModal from "./SendNotificationModal";

const NotificationTemplate = () => {
    const navigate = useNavigate();
    const primaryColor = JSON.parse(sessionStorage.getItem("vendorTheme")).theme
        .color_shades;

    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [paginationResetToggle, setPaginationResetToggle] = useState(false);
    const [myList, setMyList] = useState([]);
    const [totalRows, setTotalRows] = useState(0);

    // Filters
    const [filterNotificationType, setFilterNotificationType] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    // Image modal
    const [showImage, setShowImage] = useState(false);
    const [selectedImage, setSelectedImage] = useState("");

    // Send notification modal
    const [showSendModal, setShowSendModal] = useState(false);
    const [selectedNotificationId, setSelectedNotificationId] = useState(null);

    const [
        GetAllNotificationTemplates,
        {
            data: GetAllNotificationTemplatesData,
            isLoading: GetAllNotificationTemplatesIsLoading,
            isError: GetAllNotificationTemplatesIsError,
            error: GetAllNotificationTemplatesError,
        },
    ] = useGetAllNotificationTemplatesMutation();

    const [
        UpdateStatusOfNotificationTemplate,
        {
            data: UpdateStatusOfNotificationTemplateData,
            isLoading: UpdateStatusOfNotificationTemplateIsLoading,
            isError: UpdateStatusOfNotificationTemplateIsError,
            error: UpdateStatusOfNotificationTemplateError,
        },
    ] = useUpdateStatusOfNotificationTemplateMutation();

    const [
        SendNotification,
        {
            data: SendNotificationData,
            isLoading: SendNotificationIsLoading,
            isError: SendNotificationIsError,
            error: SendNotificationError,
        },
    ] = useSendNotificationMutation();

    const fetchTemplates = (limit, offset) => {
        const body = {
            limit: limit,
            offset: offset,
        };

        // Add filters if they exist
        if (filterNotificationType) {
            body.notification_type = filterNotificationType;
        }
        if (filterStatus) {
            body.status = filterStatus;
        }

        GetAllNotificationTemplates({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: body,
        });
    };

    useEffect(() => {
        fetchTemplates(limit, offset);
    }, [limit, offset]);

    useEffect(() => {
        if (GetAllNotificationTemplatesData) {
            toast.dismiss();
            toast.success(GetAllNotificationTemplatesData.message, {
                id: "GetAllNotificationTemplatesData",
            });

            setMyList(GetAllNotificationTemplatesData.body.data || []);
            setTotalRows(GetAllNotificationTemplatesData.body.total_count || 0);
        }
        if (GetAllNotificationTemplatesError) {
            toast.dismiss();
            toast.error("Oops some error occurred", {
                id: "GetAllNotificationTemplatesError",
            });
        }
    }, [GetAllNotificationTemplatesData, GetAllNotificationTemplatesError]);

    const handleStatusChange = (row) => {
        const newStatus = row.status === "1" ? "0" : "1";
        UpdateStatusOfNotificationTemplate({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            id: row.id,
            status: newStatus,
        });
    };

    useEffect(() => {
        if (UpdateStatusOfNotificationTemplateData) {
            toast.dismiss();
            toast.success(UpdateStatusOfNotificationTemplateData.message, {
                id: "UpdateStatusOfNotificationTemplateData",
            });

            fetchTemplates(limit, offset);
        }
        if (UpdateStatusOfNotificationTemplateError) {
            toast.dismiss();
            toast.error("Oops some error occurred", {
                id: "UpdateStatusOfNotificationTemplateError",
            });
        }
    }, [
        UpdateStatusOfNotificationTemplateData,
        UpdateStatusOfNotificationTemplateError,
    ]);

    useEffect(() => {
        if (SendNotificationData) {
            toast.dismiss();
            toast.success(SendNotificationData.message || "Notification sent successfully", {
                id: "SendNotificationData",
            });
            setShowSendModal(false);
            setSelectedNotificationId(null);
        }
        if (SendNotificationError) {
            toast.dismiss();
            toast.error(SendNotificationError?.data?.message || "Failed to send notification", {
                id: "SendNotificationError",
            });
        }
    }, [SendNotificationData, SendNotificationError]);

    const handleSendClick = (row) => {
        setSelectedNotificationId(row.id);
        setShowSendModal(true);
    };

    const handleSendNotification = (data) => {
        SendNotification({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: data,
        });
    };

    const handleApplyFilters = () => {
        setOffset(0);
        setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
        fetchTemplates(limit, 0);
    };

    const handleClearFilters = () => {
        setFilterNotificationType("");
        setFilterStatus("");
        setOffset(0);
        setCurrentPage(1);
    setPaginationResetToggle((prev) => !prev);
        // Call API with initial state (no filters)
        GetAllNotificationTemplates({
            token: sessionStorage.getItem("access_token"),
            tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            body: {
                limit: limit,
                offset: 0,
            },
        });
    };

    const handleImageClick = (imageUrl) => {
        setSelectedImage(imageUrl);
        setShowImage(true);
    };

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => offset + Number(idx + 1),
            sortable: true,
            width: "90px",
            textAlign: "left",
        },
        {
            name: "Notification Type",
            cell: (row) => row.notification_type || "-",
            sortable: true,
        },
        {
            name: "Title",
            cell: (row) => row.title || "-",
            sortable: true,
        },
        {
            name: "Body",
            cell: (row) => (
                <div className="max-w-xs truncate" title={row.body}>
                    {row.body || "-"}
                </div>
            ),
            sortable: true,
        },
        {
            name: "Image",
            cell: (row) =>
                row.image && row.image.trim() !== "" ? (
                    <button type="button" className="bg-black p-1 rounded-sm">
                        <img
                            src={view}
                            alt="view"
                            onClick={() => handleImageClick(row.image)}
                        />
                    </button>
                ) : (
                    <span className="text-gray-400 text-xs">-</span>
                ),
            sortable: false,
            width: "100px",
        },
        // {
        //     name: "Status",
        //     cell: (row) =>
        //         row.status == "1" ? (
        //             <>
        //                 <button
        //                     onClick={() => {
        //                         handleStatusChange(row);
        //                     }}
        //                     className="flex justify-center items-center rounded bg-green-900 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-green-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-green-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-green-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)]"
        //                 >
        //                     Active
        //                 </button>
        //             </>
        //         ) : (
        //             <button
        //                 onClick={() => {
        //                     handleStatusChange(row);
        //                 }}
        //                 className="flex justify-center items-center rounded bg-gray-900 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-gray-600 hover:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:bg-gray-600 focus:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)] focus:outline-none focus:ring-0 active:bg-gray-700 active:shadow-[0_8px_9px_-4px_rgba(59,113,202,0.3),0_4px_18px_0_rgba(59,113,202,0.2)]"
        //             >
        //                 InActive
        //             </button>
        //         ),
        //     sortable: true,
        //     width: "120px",
        // },
        {
            name: "Actions",
            cell: (row) => (
                row.status == "1" ? (
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleSendClick(row)}
                            className="flex justify-center items-center rounded bg-green-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#22c55e] transition duration-150 ease-in-out hover:bg-green-700"
                        >
                            <AiOutlineSend className="mr-1" />
                            Send
                        </button>
                        {/* <button
                            onClick={() =>
                                navigate(`add`, {
                                    state: {
                                        edit: true,
                                        data: row,
                                    },
                                })
                            }
                            className="flex justify-center items-center rounded bg-blue-600 px-3 pb-1.5 pt-1.5 text-xs font-medium uppercase leading-normal text-white shadow-[0_4px_9px_-4px_#3b71ca] transition duration-150 ease-in-out hover:bg-blue-700"
                        >
                            <AiOutlineEdit className="mr-1" />
                            Edit
                        </button> */}
                    </div>
                ) : (
                    <div></div>
                )
            ),
            minWidth: "200px",
        },
    ];

    const handlePageChange = (pageNumber) => {
        const newOffset = (pageNumber - 1) * limit;
        setOffset(newOffset);
        setCurrentPage(pageNumber);
    };

    const handlePerRowsChange = (newPerPage, page) => {
        setLimit(newPerPage);
    };

    return (
        <>
            <div className="flex w-full bg-gray-200 h-full justify-center ">
                <div className="w-full">
                    <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
                        Notification Templates
                        <LinksComponent />
                    </div>
                    <div className="p-3">
                        {/* Filters */}
                        <div className="p-3 bg-white shadow rounded-lg mb-3">
                            <div className="flex gap-3 items-end">
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-900 mb-1">
                                        Notification Type
                                    </label>
                                    <input
                                        type="text"
                                        className="h-10 shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                        placeholder="Enter notification type"
                                        value={filterNotificationType}
                                        onChange={(e) => setFilterNotificationType(e.target.value)}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-sm font-medium text-gray-900 mb-1">
                                        Status
                                    </label>
                                    <select
                                        className="h-10 shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                    >
                                        <option value="">All</option>
                                        <option value="1">Active</option>
                                        <option value="0">Inactive</option>
                                    </select>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleApplyFilters}
                                        className="h-10 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                    >
                                        Search
                                    </button>
                                    <button
                                        onClick={handleClearFilters}
                                        className="h-10 text-gray-700 bg-gray-200 hover:bg-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Data Table */}
                        <div className="p-3 bg-white shadow rounded-lg">
                            <DataTableComponent
                                keyField={(row) => row.id}
                                columns={columns}
                                data={myList}
                                progressPending={GetAllNotificationTemplatesIsLoading}
                                totalRows={totalRows}
                                handlePerRowsChange={handlePerRowsChange}
                                handlePageChange={handlePageChange}
                                paginationDefaultPage={currentPage}
                                paginationResetDefaultPage={paginationResetToggle}
                            ></DataTableComponent>

                            {/* Floating Add Button */}
                            {/* <div
                                className="fixed bottom-0 right-16 w-16 h-16 mr-10 mb-2 cursor-pointer"
                                id="box_btn"
                            >
                                <div className="text-right">
                                    <Menu className="relative inline-block text-left">
                                        <div>
                                            <Menu.Button
                                                onClick={() =>
                                                    navigate(`add`, {
                                                        state: {
                                                            edit: false,
                                                        },
                                                    })
                                                }
                                                className="inline-flex justify-center w-12 h-12 rounded-full text-sm font-medium text-white hover:bg-opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75"
                                                style={{ backgroundColor: `${primaryColor["700"]}` }}
                                            >
                                                <HiOutlinePlus className="w-full h-full p-3" />
                                            </Menu.Button>
                                        </div>
                                    </Menu>
                                </div>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Modal */}
            {showImage && (
                <ImageModal
                    show={showImage}
                    setShow={setShowImage}
                    size="md"
                    data={selectedImage}
                >
                    <div className="w-[400px] h-[400px] overflow-hidden rounded-lg bg-gray-200">
                        <img
                            src={selectedImage}
                            alt="notification-image"
                            className="w-full h-full object-contain"
                        />
                    </div>
                </ImageModal>
            )}

            {/* Send Notification Modal */}
            <SendNotificationModal
                isOpen={showSendModal}
                onClose={() => {
                    setShowSendModal(false);
                    setSelectedNotificationId(null);
                }}
                notificationId={selectedNotificationId}
                onSend={handleSendNotification}
            />
        </>
    );
};

export default NotificationTemplate;
